<template>
  <v-text-field
      rounded="lg"
      :error-messages="error"
      :label="translation(translationList.cifCode, 'CUI')"
      v-model="cifCode"
      :rules="cifCodeValidationRules"
      name="cifCode"
      variant="outlined"
      bg-color="surface"
      :tile="true"
  >
  </v-text-field>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, toRefs, watch} from 'vue';
import {useTranslationStore, translation} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: String as () => string | null,
    customerIndex: Number as () => number | null,
  },
  setup(props, {emit}) {
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const {modelValue, customerIndex} = toRefs(props);
    const cifCode = ref(modelValue.value ? modelValue.value : '');
    const error = ref([]);

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    watch(modelValue, (newValue) => {
      cifCode.value = newValue;
    });

    const cifCodeValidationRules = [
      (v: string) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: string) => validateRomanianCIF(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    function validateRomanianCIF(cif: string, showErrors?: boolean): boolean {
      if (!cif) {
        if (showErrors) {
          error.value = [translation(translationList.value.required, 'Acest câmp este obligatoriu')];
        }
        return false;
      }
      error.value = [];
      let cleanCif = cif.toUpperCase().replace('RO', '').replace(/\s/g, '');
      if (cleanCif.length < 2 || cleanCif.length > 10 || isNaN(parseInt(cleanCif))) return false;
      const testKey = '753217532';
      let controlNumber = parseInt(cleanCif.substr(cleanCif.length - 1));
      cleanCif = cleanCif.substr(0, cleanCif.length - 1).padStart(testKey.length, '0');
      let sum = 0;
      for (let i = 0; i < testKey.length; i++) {
        sum += parseInt(cleanCif.charAt(i)) * parseInt(testKey.charAt(i));
      }
      let calculatedControlNumber = sum * 10 % 11;
      if (calculatedControlNumber === 10) calculatedControlNumber = 0;

      const result = controlNumber === calculatedControlNumber;
      if (showErrors) {
        if (!result) {
          error.value = [translation(translationList.value.invalid_field, 'Acest câmp este invalid.')];
          return false;
        }
      }

      return result;
    }


    function debounce(func: Function, wait: number) {
      let timeout: number | undefined;
      return function(...args: any[]) {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => func.apply(this, args), wait);
      };
    }

    const debouncedValidate = debounce(async (newValue: string) => {
      const valueToValidate = newValue || '';
      const isValid = validateRomanianCIF(valueToValidate);
      if (isValid) {
        emit('update:modelValue', valueToValidate);
      } else {
        emit('update:modelValue', '');
      }
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `cif_code-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: 'cif_code', isValid });
      }
    }, 2500);

    watch(cifCode, (newValue) => {
      debouncedValidate(newValue);
    });

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      const isValid = validateRomanianCIF(cifCode.value);
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', {field: `cif_code-${customerIndex.value}`, isValid});
      } else {
        emit('validation-customer-status', {field: 'cif_code', isValid});
      }

      // Listen for validate event
      window.addEventListener('validate-customer', (data) => {
        if (data.detail && (data.detail === `cif_code-${customerIndex.value}` || data.detail === 'cif_code')) {
          const isValid = validateRomanianCIF(cifCode.value, true);
          if (isValid) {
            emit('validation-customer-status', {field: data.detail, isValid});
          }
        }
      });
    });

    return {
      cifCode,
      cifCodeValidationRules,
      translationList,
      translation,
      error,
    };
  },
});
</script>